import React, { Suspense } from "react";
import { lazyReload as lazy } from "./lib/lazyReload";
import { RouteErrorBoundary } from "./components/ChunkErrorBoundary";
import { useClearAppBadge, useRouteTitle } from "./lib/appShell";
function AppShellEffects() { useClearAppBadge(); useRouteTitle(); return null; }
import { FaceIdPrompt } from "./components/account/FaceId";
import { unstable_HistoryRouter as HistoryRouter, Routes, Route, Navigate, useSearchParams, useParams } from "react-router-dom";
import { createPrettyHistory } from "./lib/prettyUrls";

const appHistory = createPrettyHistory();
import { Toaster } from "sonner";
import { AppTabs } from "./components/AppTabs";
import "./App.css";

import { legacyTeamsPath, TEAMS_TAB_PATHS, TEAMS_SCOPED_PATHS } from "./lib/teamsRoutes";

import { AuthProvider } from "./lib/AuthContext";
import { NotifTopicsDialog } from "./components/account/NotifTopicsDialog";import { FavouritesProvider } from "./lib/FavouritesContext";
import InstallPrompt from "./components/InstallPrompt";
import UnlinkedAlert from "./components/tasks/UnlinkedAlert";
import Tasks from "./pages/Tasks";
import Changelog from "./pages/Changelog";
import NhlPlayer from "./pages/NhlPlayer";
import { AdminModeProvider } from "./lib/AdminModeContext";
import { BusinessModeProvider } from "./lib/BusinessModeContext";
import { CreatorModeProvider } from "./lib/CreatorModeContext";
import { DeviceViewProvider } from "./lib/DeviceViewContext";
import { PagePrefsProvider, usePagePrefs } from "./lib/PagePrefs";
import { AllGate } from "./components/schedule/AllGate";
import { PlayerCropProvider } from "./components/PlayerCropContext";
import { TourProvider } from "./lib/TourContext";
import { LeagueProvider, useLeague } from "./context/LeagueContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import Layout, { pwhlLandingRoute, landingForLeague } from "./components/Layout";
import MobilePullRefresh from "./components/MobilePullRefresh";
import { Loader } from "./components/Loader";
// Route-level code splitting — every page loads on demand.
const Teams = lazy(() => import("./pages/Teams"));
const LeagueOverview = lazy(() => import("./pages/LeagueOverview")); // eslint-disable-line no-unused-vars
const NhlSchedule = lazy(() => import("./pages/NhlSchedule"));
const NhlStandings = lazy(() => import("./pages/NhlStandings"));
const NhlLeaders = lazy(() => import("./pages/NhlLeaders"));
const NhlChemistry = lazy(() => import("./pages/NhlChemistry"));
const NhlDemographics = lazy(() => import("./pages/NhlDemographics"));
const NhlRosters = lazy(() => import("./pages/NhlRosters"));
const NhlLineups = lazy(() => import("./pages/NhlLineups"));
const NatSchedule = lazy(() => import("./pages/NatSchedule"));
const NcaaSchedule = lazy(() => import("./pages/NcaaSchedule"));
const NcaaStandings = lazy(() => import("./pages/NcaaStandings"));
const NatStandings = lazy(() => import("./pages/NatStandings"));
const NatLeaders = lazy(() => import("./pages/NatLeaders"));
const NhlSources = lazy(() => import("./pages/NhlSources"));
const TeamDetail = lazy(() => import("./pages/TeamDetail"));
const PlayerProfile = lazy(() => import("./pages/PlayerProfile"));
const Leaderboards = lazy(() => import("./pages/Leaderboards"));
const PlayerCompare = lazy(() => import("./pages/PlayerCompare"));
const SearchPage = lazy(() => import("./pages/Search"));
const Admin = lazy(() => import("./pages/Admin"));
const Business = lazy(() => import("./pages/Business"));
const RosterlyBusiness = lazy(() => import("./pages/RosterlyBusiness"));
const Owner = lazy(() => import("./pages/Owner"));
const StorageCentre = lazy(() => import("./pages/StorageCentre"));
const BusinessPortal = lazy(() => import("./pages/BusinessPortal"));
const NCAA = lazy(() => import("./pages/NCAA"));
const Transfers = lazy(() => import("./pages/Transfers"));
const Championships = lazy(() => import("./pages/Championships"));
const Trades = lazy(() => import("./pages/Trades"));
const Login = lazy(() => import("./pages/Login"));
const UserSetup = lazy(() => import("./components/account/UserSetup"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Draft = lazy(() => import("./pages/Draft"));
const Archive = lazy(() => import("./pages/Archive"));
const News = lazy(() => import("./pages/News"));
const Videos = lazy(() => import("./pages/Videos"));
const Roadmap = lazy(() => import("./pages/Roadmap"));
const BugReports = lazy(() => import("./pages/BugReports"));
// eslint-disable-next-line no-unused-vars
const FanWall = lazy(() => import("./pages/FanWall"));
const Socials = lazy(() => import("./pages/Socials"));
const Studio = lazy(() => import("./pages/Studio"));
const TemplateStudio = lazy(() => import("./pages/TemplateStudio"));
// No dedicated game pages: a game link opens that game in place on its schedule (?game=ID).
const GameToSchedule = ({ to }) => { const { gameId } = useParams(); return <Navigate to={`${to}?game=${encodeURIComponent(gameId)}`} replace />; };
const Records = lazy(() => import("./pages/Records"));
const Milestones = lazy(() => import("./pages/Milestones"));
const Insights = lazy(() => import("./pages/Insights"));
const Trends = null; // retired — /trends now redirects to /leaderboards (Trends is a toggle there)
const Links = null; // retired — family relationships now live on player bios; /links redirects home
const Chemistry = lazy(() => import("./pages/PwhlChemistryNhl")); // PWHL Chemistry = duplicate of NHL Chemistry
const ChemistryAll = lazy(() => import("./pages/ChemistryAll"));
const Positions = lazy(() => import("./pages/Positions"));
const RosterlyNews = lazy(() => import("./pages/RosterlyNews"));
const RosterlyVideos = lazy(() => import("./pages/RosterlyVideos"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const Messages = lazy(() => import("./pages/Messages"));
const Awards = lazy(() => import("./pages/Awards"));
const Arenas = lazy(() => import("./pages/Arenas"));
const CoachProfile = lazy(() => import("./pages/CoachProfile"));
const League = lazy(() => import("./pages/League"));
const Leagues = lazy(() => import("./pages/Leagues"));
const Rankings = lazy(() => import("./pages/Rankings"));
const Stories = lazy(() => import("./pages/Stories"));
const StoryDownload = lazy(() => import("./pages/StoryDownload"));
const Playstyles = lazy(() => import("./pages/Playstyles"));
const Packs = lazy(() => import("./pages/Packs"));
const AllSchedule = lazy(() => import("./pages/AllSchedule"));
const Pick6 = lazy(() => import("./pages/fantasy/Pick6"));
const Random6 = lazy(() => import("./pages/fantasy/Random6"));
const FantasyLeagues = lazy(() => import("./pages/fantasy/FantasyLeagues"));
const Demographics = lazy(() => import("./pages/Demographics"));
const SwitchboardHub = lazy(() => import("./pages/SwitchboardHub"));
const Olympics = lazy(() => import("./pages/Olympics"));
const MyAccount = lazy(() => import("./pages/MyAccount"));
const TeamInfo = lazy(() => import("./pages/TeamInfo"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const RulesPenalties = lazy(() => import("./pages/RulesPenalties"));

// Legacy /teams?tab=X&team=Y → clean path (e.g. /standings, /rosters/bos).
function TeamsRedirect() {
    const [sp] = useSearchParams();
    return <Navigate to={legacyTeamsPath(sp)} replace />;
}

// Landing routes a user can pin as their home page (per league).
const LANDING_ELIGIBLE = {
    pwhl: { "/standings": "Standings", "/schedule": "Schedule" },
    nhl: { "/nhl/standings": "Standings", "/nhl/schedule": "Schedule" },
};
const landingFallback = (league) => (league === "nhl" ? "/nhl/standings" : "/standings"); // eslint-disable-line no-unused-vars

// "/" is no longer a page — it forwards to the pinned landing page
// (Standings by default), per league. Set your landing via Shift+click on a nav tab.
function LandingRedirect() {
    const { getPref } = usePagePrefs();
    const { league } = useLeague();
    try { sessionStorage.setItem("rosterly_from_root", "1"); } catch { /* noop */ }
    if (league === "nhl" || league === "international") return <Navigate to={landingForLeague(league)} replace />;
    const saved = getPref("landing", "pwhl");
    const dest = (saved && LANDING_ELIGIBLE.pwhl?.[saved]) ? saved : landingForLeague("pwhl");
    return <Navigate to={dest} replace />;
}

function App() {
    return (
        <div className="App">
            <Toaster theme="dark" richColors position="top-right" closeButton />
            <LeagueProvider>
            <AuthProvider>
                <NotifTopicsDialog />
                <FavouritesProvider>
                    <PlayerCropProvider>
                    <AdminModeProvider>
                    <BusinessModeProvider>
                    <CreatorModeProvider>
                    <DeviceViewProvider>
                    <PagePrefsProvider>
                    <HistoryRouter history={appHistory}>
                        <FaceIdPrompt />
                        <AppShellEffects />
                        <TourProvider>
                        <MobilePullRefresh />
                        <AppTabs />
                        <Layout>
                            <InstallPrompt />
                            <UnlinkedAlert />
                            <RouteErrorBoundary>
                            <Suspense fallback={<Loader />}>
                            <Routes>
                                <Route path="/" element={<LandingRedirect />} />
                                <Route path="/overview" element={<Navigate to={pwhlLandingRoute()} replace />} />
                                <Route path="/teams" element={<TeamsRedirect />} />
                                {TEAMS_TAB_PATHS.map((p) => (
                                    <Route key={p} path={p} element={<Teams />} />
                                ))}
                                {TEAMS_SCOPED_PATHS.map((p) => (
                                    <Route key={`${p}-scoped`} path={`${p}/:teamId`} element={<Teams />} />
                                ))}
                                <Route path="/team-info" element={<TeamInfo />} />
                                <Route path="/teams/:teamId" element={<TeamDetail />} />
                                <Route path="/coaches/:coachId" element={<CoachProfile />} />
                                <Route path="/players/:playerId" element={<PlayerProfile />} />
                                <Route path="/leaderboards" element={<Leaderboards />} />
                                <Route path="/compare" element={<PlayerCompare />} />
                                <Route path="/records" element={<Records />} />
                                <Route path="/milestones" element={<Milestones />} />
                                <Route path="/insights" element={<Insights />} />
                                <Route path="/trends" element={<Navigate to="/leaderboards" replace />} />
                                <Route path="/links" element={<Navigate to="/" replace />} />
                                <Route path="/chemistry" element={<Chemistry />} />
                                <Route path="/chemistry/all" element={<ChemistryAll />} />
                                <Route path="/positions" element={<Positions />} />
                                <Route path="/starting-six" element={<Navigate to="/awards?tab=starting-six" replace />} />
                                <Route path="/arenas" element={<Arenas />} />
                                <Route path="/nhl" element={<Navigate to="/nhl/schedule" replace />} />
                                <Route path="/nat" element={<Navigate to="/nat/schedule" replace />} />
                                <Route path="/nat/schedule" element={<AllGate><NatSchedule /></AllGate>} />
                                <Route path="/ncaa" element={<Navigate to="/ncaa/schedule" replace />} />
                                <Route path="/ncaa/schedule" element={<AllGate><NcaaSchedule /></AllGate>} />
                                <Route path="/ncaa/standings" element={<NcaaStandings />} />
                                <Route path="/ncaa/leaderboards" element={<NhlLeaders league="ncaa" />} />
                                <Route path="/ncaa/chemistry" element={<NhlChemistry league="ncaa" />} />
                                <Route path="/all/schedule" element={<AllSchedule />} />
                                <Route path="/nat/standings" element={<NatStandings />} />
                                <Route path="/nat/leaderboards" element={<NatLeaders />} />
                                <Route path="/nhl/schedule" element={<ProtectedRoute nhlOnly><AllGate><NhlSchedule /></AllGate></ProtectedRoute>} />
                                <Route path="/nhl/standings" element={<ProtectedRoute nhlOnly><NhlStandings /></ProtectedRoute>} />
                                <Route path="/nhl/leaderboards" element={<ProtectedRoute nhlOnly><NhlLeaders /></ProtectedRoute>} />
                                <Route path="/nhl/chemistry" element={<ProtectedRoute nhlOnly><NhlChemistry /></ProtectedRoute>} />
                                <Route path="/nhl/demographics" element={<ProtectedRoute nhlOnly><NhlDemographics /></ProtectedRoute>} />
                                <Route path="/nhl/rosters" element={<ProtectedRoute nhlOnly><NhlRosters /></ProtectedRoute>} />
                                <Route path="/tasks" element={<ProtectedRoute adminOnly><Tasks /></ProtectedRoute>} />
                                <Route path="/changelog" element={<ProtectedRoute adminOnly><Changelog /></ProtectedRoute>} />
                                <Route path="/nhl/player/:playerId" element={<ProtectedRoute nhlOnly><NhlPlayer /></ProtectedRoute>} />
                                <Route path="/nhl/lineups" element={<ProtectedRoute nhlOnly><NhlLineups /></ProtectedRoute>} />
                                <Route path="/nhl/sources" element={<ProtectedRoute ownerOnly><NhlSources /></ProtectedRoute>} />
                                <Route path="/nhl/game/:gameId" element={<GameToSchedule to="/nhl/schedule" />} />
                                <Route path="/leagues" element={<Leagues />} />
                                <Route path="/leagues/:leagueId" element={<League />} />
                                <Route path="/transfers" element={<Transfers />} />
                                <Route path="/championships" element={<Championships />} />
                                <Route path="/trades" element={<Trades />} />
                                <Route path="/draft" element={<Draft />} />
                                <Route path="/predictions" element={<Navigate to="/standings" replace />} />
                                <Route path="/rankings" element={<Rankings />} />
                                <Route path="/stories" element={<Stories />} />
                                <Route path="/stories/download" element={<StoryDownload />} />
                                <Route path="/playstyles" element={<Playstyles />} />
                                <Route path="/demographics" element={<Demographics />} />
                                <Route path="/stats-switchboard" element={<Navigate to="/switchboard?tab=stats" replace />} />
                                <Route
                                    path="/switchboard"
                                    element={
                                        <ProtectedRoute ownerOnly>
                                            <SwitchboardHub />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route path="/olympics" element={<Olympics />} />
                                <Route path="/frozen-four" element={<Navigate to="/overview" replace />} />
                                <Route path="/packs" element={<Packs />} />
                                <Route path="/fantasy/pick6" element={<Pick6 />} />
                                <Route path="/fantasy/random6" element={<Random6 />} />
                                <Route path="/fantasy/draft" element={<Navigate to="/fantasy/pick6" replace />} />
                                <Route path="/fantasy/leagues" element={<FantasyLeagues />} />
                                <Route path="/fantasy" element={<Navigate to="/fantasy/pick6" replace />} />
                                <Route path="/account" element={<MyAccount />} />
                                <Route path="/archive" element={<Archive />} />
                                <Route path="/news" element={<News />} />
                                <Route path="/videos" element={<Videos />} />
                                <Route path="/roadmap" element={<Roadmap />} />
                                <Route path="/bug-reports" element={<BugReports />} />
                                {/* Fan Wall temporarily hidden — swap back to <FanWall /> to restore. */}
                                <Route path="/fan-wall" element={<Navigate to="/" replace />} />
                                <Route path="/socials" element={<Socials />} />
                                <Route path="/creator" element={<Studio />} />
                                <Route path="/template-studio" element={<TemplateStudio />} />
                                <Route path="/gallery" element={<Navigate to="/stories" replace />} />
                                <Route path="/studio" element={<Navigate to="/creator" replace />} />
                                <Route path="/socials/news" element={<RosterlyNews />} />
                                <Route path="/socials/videos" element={<RosterlyVideos />} />
                                <Route path="/contact" element={<ContactUs />} />
                                <Route path="/messages" element={<Messages />} />
                                <Route path="/terms" element={<Navigate to="/legal#terms" replace />} />
                                <Route path="/privacy" element={<Navigate to="/legal#privacy" replace />} />
                                <Route path="/legal" element={<LegalPage />} />
                                <Route path="/rules" element={<RulesPenalties />} />
                                <Route path="/awards" element={<Awards />} />
                                <Route path="/player-of-the-week" element={<Navigate to="/awards?tab=player-of-week" replace />} />
                                <Route path="/position-trends" element={<Navigate to="/chemistry" replace />} />
                                <Route path="/lineup-insights" element={<Navigate to="/chemistry" replace />} />
                                <Route path="/rosterly-insights" element={<Navigate to="/insights" replace />} />
                                <Route path="/analytics" element={<Navigate to="/records" replace />} />
                                <Route path="/search" element={<SearchPage />} />
                                <Route path="/game/:gameId" element={<GameToSchedule to="/schedule" />} />
                                <Route path="/login" element={<Login />} />
                                <Route path="/setup" element={<UserSetup />} />
                                <Route path="/reset-password" element={<ResetPassword />} />
                                <Route
                                    path="/admin"
                                    element={
                                        <ProtectedRoute adminOnly>
                                            <Admin />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/business"
                                    element={
                                        <ProtectedRoute ownerOnly>
                                            <Business />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/rosterly-business"
                                    element={
                                        <ProtectedRoute ownerOnly>
                                            <RosterlyBusiness />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/owner"
                                    element={
                                        <ProtectedRoute ownerOnly>
                                            <Owner />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/storage"
                                    element={
                                        <ProtectedRoute ownerOnly>
                                            <StorageCentre />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route path="/portal" element={<BusinessPortal />} />
                            </Routes>
                            </Suspense>
                            </RouteErrorBoundary>
                        </Layout>
                        </TourProvider>
                    </HistoryRouter>
                    </PagePrefsProvider>
                    </DeviceViewProvider>
                    </CreatorModeProvider>
                    </BusinessModeProvider>
                    </AdminModeProvider>
                    </PlayerCropProvider>
                </FavouritesProvider>
            </AuthProvider>
            </LeagueProvider>
        </div>
    );
}

export default App;
